import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PAIN_MAPPING_STATES } from '../types/painMapping.types';

const FIRST_IDLE_TIMEOUT_MS = 10000;
const SECOND_IDLE_TIMEOUT_MS = 10000;

function normalizeGender(gender) {
  return gender === 'woman' ? 'female' : 'male';
}

function toLocation(selection, fallbackView) {
  if (!selection) {
    return {
      view: fallbackView,
      coordinates: null,
      zone: null,
      zone_label: null,
      zone_confidence: null,
      summary: null,
      laterality: null,
      circle: null,
    };
  }
  return {
    view: selection.view || fallbackView,
    coordinates: selection.coordinates || null,
    points: selection.points || null,
    zone: selection.zone?.code || null,
    zone_id: selection.zone?.id || null,
    zone_label: selection.zone?.label || null,
    zone_confidence: selection.zone?.confidence ?? null,
    summary: selection.zone?.summary || null,
    precise_summary: selection.zone?.precise_summary || selection.zone?.summary || null,
    laterality: selection.zone?.laterality || null,
    vertical_band: selection.zone?.vertical_band || null,
    focal_point: selection.zone?.focal_point || selection.coordinates || null,
    zone_bounds: selection.zone?.bounds || null,
    circle: selection.circle || null,
  };
}

export default function usePainMapping({
  gender,
  view,
  captureEnabled = false,
  captureSessionKey = 0,
  onLocationConfirmed,
  onCapturePrompt,
  onCaptureExpired,
}) {
  const [state, setState] = useState(PAIN_MAPPING_STATES.IDLE);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [primaryLocation, setPrimaryLocation] = useState(null);
  const [secondaryLocation, setSecondaryLocation] = useState(null);
  const [blockedInteractionMessage, setBlockedInteractionMessage] = useState('');
  const [fatigueHint, setFatigueHint] = useState('');
  const [outputPayload, setOutputPayload] = useState(null);
  const dragTimerRef = useRef(null);
  const idlePromptTimerRef = useRef(null);
  const moveOnTimerRef = useRef(null);

  const notifyBlockedInteraction = useCallback(() => {
    setBlockedInteractionMessage('');
  }, []);

  const resetFatigueTimer = useCallback(() => {
    if (dragTimerRef.current) {
      clearTimeout(dragTimerRef.current);
    }
    dragTimerRef.current = setTimeout(() => {
      setFatigueHint('Take your time. Just roughly circle the area where it hurts most.');
    }, 10000);
  }, []);

  const clearFatigueTimer = useCallback(() => {
    if (dragTimerRef.current) {
      clearTimeout(dragTimerRef.current);
      dragTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearFatigueTimer(), [clearFatigueTimer]);

  const clearCaptureTimers = useCallback(() => {
    if (idlePromptTimerRef.current) {
      clearTimeout(idlePromptTimerRef.current);
      idlePromptTimerRef.current = null;
    }
    if (moveOnTimerRef.current) {
      clearTimeout(moveOnTimerRef.current);
      moveOnTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearCaptureTimers(), [clearCaptureTimers]);

  useEffect(() => {
    if (!captureEnabled) {
      clearCaptureTimers();
      setPendingSelection(null);
      setState((prev) =>
        prev === PAIN_MAPPING_STATES.PENDING_CONFIRMATION || prev === PAIN_MAPPING_STATES.AWAITING_LOCATION
          ? PAIN_MAPPING_STATES.IDLE
          : prev
      );
      return;
    }

    setPendingSelection(null);
    setPrimaryLocation(null);
    setSecondaryLocation(null);
    setOutputPayload(null);
    setBlockedInteractionMessage('');
    setFatigueHint('');
    setState(PAIN_MAPPING_STATES.AWAITING_LOCATION);
  }, [captureEnabled, captureSessionKey, clearCaptureTimers]);

  useEffect(() => {
    if (!captureEnabled || state !== PAIN_MAPPING_STATES.AWAITING_LOCATION || pendingSelection || primaryLocation) {
      clearCaptureTimers();
      return;
    }

    idlePromptTimerRef.current = setTimeout(() => {
      onCapturePrompt?.('gentle');
      moveOnTimerRef.current = setTimeout(() => {
        onCapturePrompt?.('move_on');
        setState(PAIN_MAPPING_STATES.IDLE);
        onCaptureExpired?.();
      }, SECOND_IDLE_TIMEOUT_MS);
    }, FIRST_IDLE_TIMEOUT_MS);

    return clearCaptureTimers;
  }, [
    captureEnabled,
    state,
    pendingSelection,
    primaryLocation,
    onCapturePrompt,
    onCaptureExpired,
    clearCaptureTimers,
  ]);

  const setPendingLocation = useCallback(
    (selection) => {
      if (!captureEnabled) {
        return false;
      }
      if (state === PAIN_MAPPING_STATES.PENDING_CONFIRMATION && pendingSelection) {
        return false;
      }
      if (state !== PAIN_MAPPING_STATES.AWAITING_LOCATION) {
        return false;
      }

      setBlockedInteractionMessage('');
      setFatigueHint('');
      setPendingSelection(selection);
      setState(PAIN_MAPPING_STATES.PENDING_CONFIRMATION);
      return true;
    },
    [captureEnabled, state, pendingSelection]
  );

  const clearLocation = useCallback(() => {
    clearCaptureTimers();
    setPendingSelection(null);
    setPrimaryLocation(null);
    setSecondaryLocation(null);
    setOutputPayload(null);
    setBlockedInteractionMessage('');
    setFatigueHint('');
    setState(captureEnabled ? PAIN_MAPPING_STATES.AWAITING_LOCATION : PAIN_MAPPING_STATES.IDLE);
  }, [captureEnabled, clearCaptureTimers]);

  const confirmLocation = useCallback(() => {
    if (!pendingSelection) return;
    clearCaptureTimers();
    setPrimaryLocation(pendingSelection);
    setSecondaryLocation(null);
    const payload = {
      view,
      gender: normalizeGender(gender),
      primary_location: toLocation(pendingSelection, view),
      timestamp: new Date().toISOString(),
    };
    setOutputPayload(payload);
    onLocationConfirmed?.(payload);
    setPendingSelection(null);
    setBlockedInteractionMessage('');
    setState(PAIN_MAPPING_STATES.COMPLETE);
    onCaptureExpired?.();
  }, [pendingSelection, gender, view, onLocationConfirmed, onCaptureExpired, clearCaptureTimers]);

  const resetMapForViewChange = useCallback(() => {
    if (!pendingSelection) return;
    setPendingSelection(null);
    setBlockedInteractionMessage('');
    setState(captureEnabled ? PAIN_MAPPING_STATES.AWAITING_LOCATION : PAIN_MAPPING_STATES.IDLE);
  }, [pendingSelection, captureEnabled]);

  const interactionLocked = useMemo(
    () => state === PAIN_MAPPING_STATES.PENDING_CONFIRMATION || !captureEnabled,
    [state, captureEnabled]
  );

  return {
    state,
    pendingSelection,
    primaryLocation,
    secondaryLocation,
    blockedInteractionMessage,
    fatigueHint,
    outputPayload,
    interactionLocked,
    setPendingLocation,
    clearLocation,
    confirmLocation,
    chooseRadiation: () => {},
    setPainQuality: () => false,
    setPainIntensity: () => false,
    skipPainMapping: () => {},
    resetMapForViewChange,
    notifyBlockedInteraction,
    resetFatigueTimer,
    clearFatigueTimer,
  };
}

